import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Users, Search, Mail, Phone, LayoutDashboard,
  GraduationCap, Shield, User, Building, CreditCard, Award, Target,
  Eye, EyeOff, AlertTriangle, Grid3X3, UserCheck, UserX, Calendar,
  Clock, BookOpen, CheckCircle, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EmployeesManagement = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Unified employee data (combines teachers, employees, users)
  const [employees, setEmployees] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(true); // Show inactive by default to see all
  
  // Modal states
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Employee Dashboard Modal states
  const [showEmployeeDashboardModal, setShowEmployeeDashboardModal] = useState(false);
  const [selectedEmployeeForDashboard, setSelectedEmployeeForDashboard] = useState(null);
  const [employeeDashboardData, setEmployeeDashboardData] = useState(null);
  
  // Form data for unified employee creation
  const [employeeFormData, setEmployeeFormData] = useState({
    // Basic Info (all roles)
    name: '',
    email: '',
    phone: '',
    role: 'teaching_staff', // teaching_staff, admin (removed non_teaching_staff)
    
    // Academic Info (teaching staff only)
    subjects: [],
    qualifications: '',
    experience_years: '',
    specialization: '',
    
    // Employment Info (all roles) - MOVED OFFICE HOURS HERE
    departments: [],
    designation: '',
    employee_id: '',
    date_of_joining: '',
    employment_type: 'full_time',
    salary_type: 'monthly',
    base_salary: '',
    professional_tax: 200,
    visiting_faculty: false,
    office_hours: '',
    time_in: '',
    time_out: '',
    week_offs: [],
    
    // Bank Details (all roles)
    bank_account: '',
    ifsc_code: '',
    bank_name: '',
    
    // Login Details (created by super admin only)
    user_id: '',
    password: '',
    is_admin: false,
    
    // System fields
    created_by: user?.email || ''
  });

  // Reset form when modal closes
  const resetForm = () => {
    setEmployeeFormData({
      name: '',
      email: '',
      phone: '',
      role: 'teaching_staff',
      subjects: [],
      qualifications: '',
      experience_years: '',
      specialization: '',
      departments: [],
      designation: '',
      employee_id: '',
      date_of_joining: '',
      employment_type: 'full_time',
      salary_type: 'monthly',
      base_salary: '',
      professional_tax: 200,
      visiting_faculty: false,
      office_hours: '',
      time_in: '',
      time_out: '',
      week_offs: [],
      bank_account: '',
      ifsc_code: '',
      bank_name: '',
      user_id: '',
      password: '',
      is_admin: false,
      created_by: user?.email || ''
    });
  };

  // Load employees and subjects
  useEffect(() => {
    fetchEmployees();
    fetchSubjects();
  }, [showInactive]); // Re-fetch when showInactive changes

  const fetchEmployees = async () => {
    console.log('[FETCH] Starting to fetch employees...');
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('[FETCH] No auth token found!');
        toast.error('Not authenticated. Please login again.');
        return;
      }
      
      console.log('[FETCH] Calling API: /staff/unified with include_inactive:', showInactive);
      // Call unified staff endpoint - single API call, backend handles merging
      const response = await axios.get(`${API}/staff/unified?include_inactive=${showInactive}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('[FETCH] API response:', response.data);
      const staffData = response.data?.staff || [];
      console.log(`[FETCH] Got ${staffData.length} staff members`);
      
      // Debug: Check for missing names
      const missingNames = staffData.filter(e => !e.name);
      if (missingNames.length > 0) {
        console.warn('[FETCH] Employees with missing names:', missingNames.map(e => ({ id: e.id, email: e.email })));
      }
      
      setEmployees(staffData);
      if (staffData.length > 0) {
        toast.success(`Loaded ${staffData.length} staff members`);
      } else {
        console.warn('[FETCH] No staff members returned');
        toast.info('No staff members found');
      }
    } catch (error) {
      console.error('[FETCH] Error fetching staff:', error);
      console.error('[FETCH] Error details:', error.response?.data || error.message);
      toast.error(`Failed to load staff: ${error.response?.data?.detail || error.message}`);
      setEmployees([]);
    } finally {
      setLoading(false);
      console.log('[FETCH] Fetch complete');
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const subjectsData = response.data || [];
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error('Failed to load subjects');
      setSubjects([]);
    }
  };

  // Generate employee ID
  const generateEmployeeId = () => {
    try {
      // Generate IETA/NIF/XXX format ID
      const timestamp = Date.now().toString().slice(-3); // Last 3 digits of timestamp
      const randomNum = Math.floor(Math.random() * 900) + 100; // Random 3 digit number
      const employeeId = `IETA/NIF/${timestamp}${randomNum}`.slice(0, 12); // Ensure max 12 chars
      
      setEmployeeFormData(prev => ({
        ...prev,
        employee_id: employeeId
      }));
      
      toast.success('Employee ID generated successfully');
    } catch (error) {
      console.error('Error generating employee ID:', error);
      toast.error('Failed to generate employee ID');
    }
  };

  // Employee Dashboard Handler
  const handleOpenEmployeeDashboard = async (employee) => {
    try {
      console.log('Opening employee dashboard for:', employee.name, employee.id);
      setSelectedEmployeeForDashboard(employee);
      const token = localStorage.getItem('token');
      
      // Set basic employee data first and open modal immediately
      setEmployeeDashboardData({
        leaves: {},
        schedule: [],
        attendance: [],
        employee: employee
      });
      setShowEmployeeDashboardModal(true);
      
      // Fetch comprehensive employee dashboard data in background
      try {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        
        // If employee has teacher_id, fetch teacher data to get subjects
        let teacherData = null;
        if (employee.teacher_id) {
          try {
            const teacherRes = await axios.get(`${API}/teachers/${employee.teacher_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            teacherData = teacherRes.data;
            console.log('Teacher data fetched:', teacherData);
          } catch (err) {
            console.log('Teacher fetch error:', err.message);
          }
        }
        
        const [leavesRes, scheduleRes, attendanceRes] = await Promise.all([
          axios.get(`${API}/payroll/leave-stats/${employee.id}/${new Date().toISOString().slice(0, 7)}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch((err) => {
            console.log('Leave stats API not available:', err.message);
            return { data: {} };
          }),
          // Fetch today's sessions for this teacher
          axios.get(`${API}/sessions`, {
            params: {
              teacher_id: employee.teacher_id || employee.id,
              date_from: today,
              date_to: today
            },
            headers: { Authorization: `Bearer ${token}` }
          }).catch((err) => {
            console.log('Sessions API error:', err.message);
            return { data: [] };
          }),
          // Attendance API call (if available)
          axios.get(`${API}/attendance/employee/${employee.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch((err) => {
            console.log('Attendance API not available:', err.message);
            return { data: [] };
          })
        ]);
        
        console.log('Dashboard data loaded successfully:', leavesRes.data);
        console.log('Schedule data received:', scheduleRes.data);
        
        // Filter sessions for TODAY only
        const todaySessions = Array.isArray(scheduleRes.data)
          ? scheduleRes.data.filter(session => session.scheduled_date === today)
          : [];
        
        console.log(`Filtered ${scheduleRes.data?.length || 0} total sessions to ${todaySessions.length} sessions for today (${today})`);
        
        // Deduplicate today's sessions by id or unique combination
        const uniqueTodaySessions = todaySessions.filter((session, index, self) =>
          index === self.findIndex((s) => 
            s.id === session.id || 
            (s.time_slot === session.time_slot && 
             s.subject_name === session.subject_name && 
             s.classroom_name === session.classroom_name &&
             s.batch_name === session.batch_name)
          )
        );
        
        console.log(`Deduplicated to ${uniqueTodaySessions.length} unique sessions for today`);
        
        // Get unique subject IDs from ALL sessions (not just today) for this teacher
        const allSessionSubjectIds = Array.isArray(scheduleRes.data)
          ? [...new Set(scheduleRes.data.map(s => s.subject_id).filter(Boolean))]
          : [];
        
        console.log(`Found ${allSessionSubjectIds.length} unique subject IDs from sessions:`, allSessionSubjectIds);
        
        // Merge employee data with teacher data and use session subject IDs
        const enrichedEmployee = {
          ...employee,
          // Use subject IDs from actual sessions (these are real, active subjects)
          subjects: allSessionSubjectIds.length > 0 ? allSessionSubjectIds : (teacherData?.subjects || employee.subjects || []),
          role: teacherData ? 'teaching_staff' : (employee.role || 'administration'),
          teacher_data: teacherData
        };
        
        console.log('Enriched employee:', enrichedEmployee);
        console.log('Active subjects from sessions:', allSessionSubjectIds);
        
        // Update with fetched data
        setEmployeeDashboardData({
          leaves: leavesRes.data || {},
          schedule: uniqueTodaySessions, // Only today's sessions
          attendance: attendanceRes.data || [],
          employee: enrichedEmployee
        });
      } catch (dataError) {
        console.error('Error fetching dashboard data:', dataError);
        // Don't show error toast since modal is already open with basic info
      }
    } catch (error) {
      console.error('Error opening employee dashboard:', error);
      toast.error('Failed to open employee dashboard');
    }
  };

  // Enable/Disable User Handler
  const handleToggleEmployeeStatus = async (employee) => {
    const newStatus = !employee.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} ${employee.name}? This will also ${action} their user account and login access.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log(`[ACTIVATE] Toggling ${employee.name} to ${newStatus}`);
      
      // Use unified activate endpoint - syncs employee + user + teacher
      const response = await axios.patch(
        `${API}/staff/unified/${employee.id}/activate?is_active=${newStatus}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(`[ACTIVATE] Success response:`, response.data);
      toast.success(`Staff member ${action}d successfully`);
      
      // Refresh data
      console.log(`[ACTIVATE] Refreshing staff list...`);
      await fetchEmployees();
      console.log(`[ACTIVATE] Refresh complete`);
    } catch (error) {
      console.error('[ACTIVATE] Error details:', error);
      console.error('[ACTIVATE] Error response:', error.response);
      toast.error(error.response?.data?.detail || `Failed to ${action} staff member`);
    }
  };

  // Generate user credentials
  const generateUserCredentials = () => {
    // Use email as user_id
    const userId = employeeFormData.email || '';
    
    // Generate default password as FirstName@123
    const firstName = employeeFormData.name.split(' ')[0] || 'User';
    const password = `${firstName}@123`;
    
    setEmployeeFormData(prev => ({
      ...prev,
      user_id: userId,
      password: password
    }));
    
    if (!userId) {
      toast.error('Please enter email first');
    } else {
      toast.success(`Default password set to: ${password}`);
    }
  };

  const handleSaveEmployee = async () => {
    try {
      // Validation
      if (!employeeFormData.name || !employeeFormData.email) {
        toast.error('Name and email are required');
        return;
      }

      if (employeeFormData.departments.length === 0) {
        toast.error('At least one department must be selected');
        return;
      }

      const token = localStorage.getItem('token');

      // Prepare employee data - match backend Pydantic model exactly
      const employeeData = {
        name: employeeFormData.name,
        email: employeeFormData.email,
        phone: employeeFormData.phone || '',
        role: employeeFormData.role,
        designation: employeeFormData.designation || '',
        employee_id: employeeFormData.employee_id,
        joining_date: employeeFormData.date_of_joining || new Date().toISOString().split('T')[0],
        department: Array.isArray(employeeFormData.departments) 
          ? (employeeFormData.departments[0] || 'General') 
          : (employeeFormData.departments || 'General'),
        employment_type: employeeFormData.visiting_faculty ? 'Visiting Faculty' : 
                        (employeeFormData.salary_type === 'monthly' ? 'Full-time' : 'Part-time'),
        salary_type: employeeFormData.salary_type === 'monthly' ? 'Monthly' : 
                    (employeeFormData.salary_type === 'hourly' ? 'Hourly' : 'Monthly'),
        base_salary: parseFloat(employeeFormData.base_salary) || 0,
        hourly_rate: parseFloat(employeeFormData.hourly_rate) || 0,
        professional_tax: parseFloat(employeeFormData.professional_tax) || 200,
        // Flatten time and week_offs (not nested in office_hours)
        time_in: employeeFormData.time_in || '09:00',
        time_out: employeeFormData.time_out || '17:00',
        week_offs: Array.isArray(employeeFormData.week_offs) 
          ? employeeFormData.week_offs.map(day => day.toLowerCase()) 
          : [],
        leave_policy: {
          paid_leaves_per_month: 1,
          carry_forward: true
        },
        bank_details: {
          account_holder_name: employeeFormData.name || '',
          account_number: employeeFormData.bank_account || '',
          ifsc_code: employeeFormData.ifsc_code || '',
          bank_name: employeeFormData.bank_name || '',
          account_type: 'Savings'
        },
        subjects: employeeFormData.subjects || [],
        qualifications: employeeFormData.qualifications || '',
        experience_years: parseInt(employeeFormData.experience_years) || 0,
        specialization: employeeFormData.specialization || '',
        created_by: user?.email || 'admin'
      };

      if (editingEmployee) {
        // Update using unified endpoint - backend handles employee + user + teacher sync
        const updateData = {
          ...employeeData,
          password: employeeFormData.password || undefined, // Only if password is being changed
          is_admin: employeeFormData.is_admin || false
        };
        
        console.log('[UPDATE STAFF] Data being sent:', updateData);
        
        await axios.put(`${API}/staff/unified/${editingEmployee.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Unified endpoint handles teacher and user account sync automatically
        toast.success('Staff member updated successfully');
      } else {
        // Create using unified endpoint - backend creates employee + user account together
        if (!employeeFormData.password) {
          toast.error('Password is required for new staff members');
          return;
        }
        
        const createData = {
          ...employeeData,
          password: employeeFormData.password,
          is_admin: employeeFormData.is_admin || false
        };
        
        const response = await axios.post(`${API}/staff/unified`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Unified endpoint creates employee + user account + teacher record automatically
        toast.success(`Staff member added successfully! They can now login with:\nEmail: ${employeeFormData.email}\nPassword: ${employeeFormData.password}`);
      }

      // Refresh data and close modal
      await fetchEmployees();
      setIsAddEmployeeModalOpen(false);
      setEditingEmployee(null);
      resetForm();

    } catch (error) {
      console.error('Error saving employee:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to save employee';
      toast.error(errorMessage);
    }
  };

  const handleEditEmployee = (employee) => {
    console.log('[EDIT EMPLOYEE] Loading employee for editing:', employee.name);
    console.log('[EDIT EMPLOYEE] Raw employee data:', employee);
    console.log('[EDIT EMPLOYEE] Current role:', employee.role);
    console.log('[EDIT EMPLOYEE] Current department (backend):', employee.department);
    console.log('[EDIT EMPLOYEE] Current departments (frontend):', employee.departments);
    console.log('[EDIT EMPLOYEE] Current time_in:', employee.time_in);
    console.log('[EDIT EMPLOYEE] Current time_out:', employee.time_out);
    console.log('[EDIT EMPLOYEE] Current week_offs:', employee.week_offs);
    
    // Convert backend department (singular string) to frontend departments (array)
    let departmentsArray = [];
    if (employee.departments && Array.isArray(employee.departments)) {
      departmentsArray = employee.departments;
    } else if (employee.department && typeof employee.department === 'string') {
      // Backend uses singular 'department' as string, convert to array
      departmentsArray = [employee.department];
    }
    
    // Convert backend week_offs (lowercase) to frontend format (capitalized)
    let weekOffsArray = [];
    if (employee.week_offs && Array.isArray(employee.week_offs)) {
      // Backend stores as lowercase 'sunday', convert to 'Sunday'
      weekOffsArray = employee.week_offs.map(day => 
        day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
      );
    }
    
    console.log('[EDIT EMPLOYEE] Converted departments:', departmentsArray);
    console.log('[EDIT EMPLOYEE] Converted week_offs:', weekOffsArray);
    
    // Load employee data into form with explicit value handling
    setEmployeeFormData({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      role: employee.role || 'teaching_staff',  // Default to teaching_staff if not set
      subjects: employee.subjects || [],
      qualifications: employee.qualifications || '',
      experience_years: employee.experience_years || '',
      specialization: employee.specialization || '',
      departments: departmentsArray,
      designation: employee.designation || '',
      employee_id: employee.employee_id || '',
      date_of_joining: employee.joining_date || employee.date_of_joining || '',
      employment_type: employee.employment_type || 'Full-time',
      salary_type: employee.salary_type || 'Monthly',
      base_salary: employee.base_salary || '',
      professional_tax: employee.professional_tax || 200,
      visiting_faculty: employee.employment_type === 'Visiting Faculty' || employee.visiting_faculty || false,
      office_hours: employee.office_hours || '',
      time_in: employee.time_in || '09:00',
      time_out: employee.time_out || '18:00',
      week_offs: weekOffsArray,
      bank_account: employee.bank_details?.account_number || employee.bank_account || '',
      ifsc_code: employee.bank_details?.ifsc_code || employee.ifsc_code || '',
      bank_name: employee.bank_details?.bank_name || employee.bank_name || '',
      user_id: employee.user_id || '',
      password: '', // Leave empty, actual password won't be changed unless user enters new one
      is_admin: employee.is_admin || false,
      created_by: employee.created_by || ''
    });
    
    console.log('[EDIT EMPLOYEE] Form data set successfully with departments:', departmentsArray, 'and week_offs:', weekOffsArray);
    
    setEditingEmployee(employee);
    setIsAddEmployeeModalOpen(true);
  };

  const handleRemoveDuplicates = async () => {
    if (!window.confirm('⚠️ REMOVE DUPLICATE EMPLOYEES\n\nThis will:\n• Keep the newest record for each email\n• Delete older duplicate records\n• Clean up related teacher and user records\n\nThis action CANNOT be undone.\n\nAre you sure you want to continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/staff/unified/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const message = `✅ Cleanup Complete!\n• Removed ${response.data.removed_count || 0} duplicate records\n• Cleaned ${response.data.cleaned_emails?.length || 0} emails`;
      toast.success(message);
      
      await fetchEmployees(); // Refresh the list
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove duplicates');
    }
  };

  // Handle role change (grant/revoke admin rights)
  const handleChangeRole = async (employee) => {
    const currentRole = employee.user_role || 'teacher';
    const isCurrentlyAdmin = currentRole === 'admin' || currentRole === 'super_admin';
    const newIsAdmin = !isCurrentlyAdmin;
    const action = newIsAdmin ? 'grant admin rights to' : 'revoke admin rights from';
    
    if (!window.confirm(`⚠️ CHANGE ADMIN RIGHTS\n\nUser: ${employee.name}\nCurrent: ${isCurrentlyAdmin ? 'Admin' : 'Teacher'}\nNew: ${newIsAdmin ? 'Admin' : 'Teacher'}\n\nAre you sure you want to ${action} ${employee.name}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Use unified update endpoint to change role
      await axios.put(
        `${API}/staff/unified/${employee.id}`,
        {
          ...employee,
          is_admin: newIsAdmin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Admin rights ${newIsAdmin ? 'granted' : 'revoked'} successfully`);
      await fetchEmployees();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };


  // Filter employees based on search and role
  const filteredEmployees = employees.filter(employee => {
    // Handle missing data gracefully
    const name = employee.name || '';
    const email = employee.email || '';
    const employeeId = employee.employee_id || '';
    
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || employee.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Get subjects for a teaching staff employee
  const getEmployeeSubjects = (employeeSubjects) => {
    if (!employeeSubjects || employeeSubjects.length === 0) return [];
    return subjects.filter(subject => employeeSubjects.includes(subject.id));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading employees...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees Management</h1>
          <p className="text-gray-600">Manage teaching staff and administration employees</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button 
              onClick={handleRemoveDuplicates}
              className="bg-orange-600 hover:bg-orange-700"
              title="Permanently remove duplicate employee records"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove Duplicates
            </Button>
          )}
          <Button 
            onClick={() => {
              resetForm();
              setEditingEmployee(null);
              setIsAddEmployeeModalOpen(true);
              generateEmployeeId();
              // Password will be set when "Generate Password" is clicked or defaults to FirstName@123
              setEmployeeFormData(prev => ({
                ...prev,
                password: ''
              }));
            }}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Employee</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search employees by name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="teaching_staff">Teaching Staff</SelectItem>
                  <SelectItem value="admin">Administration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    fetchEmployees();
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show Inactive</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employees Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredEmployees.map(employee => (
          <Card key={employee.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">
                    {employee.name?.charAt(0)?.toUpperCase() || 'E'}
                  </span>
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{employee.name}</CardTitle>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">
                      {employee.role === 'teaching_staff' ? 'Teaching Staff' : 'Administration'}
                    </Badge>
                    {employee.user_role && (
                      <Badge 
                        variant={employee.user_role === 'super_admin' ? 'default' : employee.user_role === 'admin' ? 'secondary' : 'outline'} 
                        className="text-xs"
                      >
                        {employee.user_role === 'super_admin' ? '👑 Super Admin' : employee.user_role === 'admin' ? '🔑 Admin' : '👤 Teacher'}
                      </Badge>
                    )}
                    {employee.user_status && (
                      <Badge 
                        variant={employee.user_status === 'approved' ? 'default' : 'destructive'}
                        className="text-xs"
                      >
                        {employee.user_status === 'approved' ? '✓ Active' : '⊘ Inactive'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Building className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">
                    {employee.departments?.join(', ') || employee.department || 'No department'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700">{employee.employee_id || 'No ID'}</span>
                </div>
                
                {/* Show assigned subjects if any exist */}
                {employee.subjects && employee.subjects.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700 font-medium">Assigned Subjects:</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {getEmployeeSubjects(employee.subjects).map(subject => (
                        <Badge key={subject.id} variant="secondary" className="text-xs">
                          {subject.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-2 pt-2">
                {/* First Row */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenEmployeeDashboard(employee)}
                  className="flex items-center justify-center"
                >
                  <Grid3X3 className="w-3 h-3 mr-1" />
                  Dashboard
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditEmployee(employee)}
                  className="flex items-center justify-center"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                
                {/* Second Row - Activate/Deactivate and Change Role */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleEmployeeStatus(employee)}
                  className={`flex items-center justify-center ${
                    employee.is_active === false 
                      ? 'text-green-600 hover:text-green-700' 
                      : 'text-orange-600 hover:text-orange-700'
                  }`}
                  title="Activate/Deactivate employee - Controls employee status, login access, and visibility in all systems"
                >
                  {employee.is_active === false ? (
                    <>
                      <UserCheck className="w-3 h-3 mr-1" />
                      Activate
                    </>
                  ) : (
                    <>
                      <UserX className="w-3 h-3 mr-1" />
                      Deactivate
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangeRole(employee)}
                  className="flex items-center justify-center text-blue-600 hover:text-blue-700"
                  title="Change User Role (Grant/Revoke Admin Rights)"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Change Role
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
            <p className="text-gray-600">
              {searchTerm || roleFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Get started by adding your first employee'
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Employee Modal */}
      <Dialog open={isAddEmployeeModalOpen} onOpenChange={setIsAddEmployeeModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="employment">Employment</TabsTrigger>
              <TabsTrigger value="academic">Academic</TabsTrigger>
              <TabsTrigger value="login">Login Details</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="Enter full name"
                    value={employeeFormData.name}
                    onChange={(e) => setEmployeeFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="Enter email"
                    value={employeeFormData.email}
                    onChange={(e) => setEmployeeFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="Enter phone number"
                    value={employeeFormData.phone}
                    onChange={(e) => setEmployeeFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Role *</Label>
                  <Select
                    value={employeeFormData.role}
                    onValueChange={(value) => setEmployeeFormData(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teaching_staff">Teaching Staff</SelectItem>
                      <SelectItem value="admin">Administration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bank Details */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Bank Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bank Account Number</Label>
                    <Input
                      placeholder="Account number"
                      value={employeeFormData.bank_account}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, bank_account: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      placeholder="IFSC code"
                      value={employeeFormData.ifsc_code}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, ifsc_code: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      placeholder="Bank name"
                      value={employeeFormData.bank_name}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, bank_name: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Employment Info Tab - OFFICE HOURS MOVED HERE */}
            <TabsContent value="employment" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Departments * (Can select multiple)</Label>
                  <div className="space-y-2 mt-2 max-h-32 overflow-y-auto border rounded p-3">
                    {[
                      'Fashion Design',
                      'Interior Design',
                      'MBA Design Management',
                      'Skill Enhancement',
                      'Administration'
                    ].map((dept) => (
                      <label key={dept} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={employeeFormData.departments && employeeFormData.departments.includes(dept)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEmployeeFormData(prev => ({
                                ...prev,
                                departments: [...(prev.departments || []), dept]
                              }));
                            } else {
                              setEmployeeFormData(prev => ({
                                ...prev,
                                departments: (prev.departments || []).filter(d => d !== dept)
                              }));
                            }
                          }}
                          className="rounded"
                        />
                        <span>{dept}</span>
                      </label>
                    ))}
                  </div>
                  {employeeFormData.departments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {employeeFormData.departments.map(dept => (
                        <Badge key={dept} variant="outline" className="text-xs">
                          {dept}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Designation</Label>
                  <Input
                    placeholder="e.g., Assistant Professor, HOD, Manager"
                    value={employeeFormData.designation}
                    onChange={(e) => setEmployeeFormData(prev => ({ ...prev, designation: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Employee ID</Label>
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Auto-generated"
                      value={employeeFormData.employee_id}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={generateEmployeeId}
                      className="whitespace-nowrap"
                    >
                      Generate
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Date of Joining</Label>
                  <Input
                    type="date"
                    value={employeeFormData.date_of_joining}
                    onChange={(e) => setEmployeeFormData(prev => ({ ...prev, date_of_joining: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Salary Type</Label>
                  <Select
                    value={employeeFormData.salary_type}
                    onValueChange={(value) => setEmployeeFormData(prev => ({ ...prev, salary_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select salary type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="hourly">Hourly (Visiting Faculty)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* OFFICE HOURS SECTION - MOVED FROM ACADEMIC TAB */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Office Hours & Work Schedule</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Office Hours Description</Label>
                    <Textarea
                      placeholder="e.g., Monday-Friday 9:00 AM - 5:00 PM"
                      value={employeeFormData.office_hours}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, office_hours: e.target.value }))}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Time In/Out</Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-sm">Time In</Label>
                        <Select
                          value={employeeFormData.time_in}
                          onValueChange={(value) => setEmployeeFormData(prev => ({ ...prev, time_in: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select time in" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour = i.toString().padStart(2, '0');
                              return (
                                <SelectItem key={`${hour}:00`} value={`${hour}:00`}>
                                  {hour}:00
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Time Out</Label>
                        <Select
                          value={employeeFormData.time_out}
                          onValueChange={(value) => setEmployeeFormData(prev => ({ ...prev, time_out: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select time out" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour = i.toString().padStart(2, '0');
                              return (
                                <SelectItem key={`${hour}:00`} value={`${hour}:00`}>
                                  {hour}:00
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Working Days Selection - Unselected = Week Offs */}
                <div className="mt-4">
                  <Label>Working Days (Unselected days will be considered as week offs)</Label>
                  <div className="grid grid-cols-7 gap-2 mt-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <label key={day} className="flex flex-col items-center p-2 border rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!employeeFormData.week_offs || !employeeFormData.week_offs.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Remove from week_offs (mark as working day)
                              setEmployeeFormData(prev => ({
                                ...prev,
                                week_offs: (prev.week_offs || []).filter(d => d !== day)
                              }));
                            } else {
                              // Add to week_offs (mark as week off)
                              setEmployeeFormData(prev => ({
                                ...prev,
                                week_offs: [...(prev.week_offs || []), day]
                              }));
                            }
                          }}
                          className="rounded mb-1"
                        />
                        <span className="text-xs text-center">{day.slice(0, 3)}</span>
                        <span className="text-xs text-gray-500">
                          {(!employeeFormData.week_offs || !employeeFormData.week_offs.includes(day)) ? 'Work' : 'Off'}
                        </span>
                      </label>
                    ))}
                  </div>
                  {employeeFormData.week_offs && employeeFormData.week_offs.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">Week offs: </span>
                      {employeeFormData.week_offs.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Salary Details */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Salary Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Base Salary (₹)</Label>
                    <Input
                      type="number"
                      placeholder="Enter base salary"
                      value={employeeFormData.base_salary}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, base_salary: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Professional Tax (₹)</Label>
                    <Input
                      type="number"
                      value={employeeFormData.professional_tax}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, professional_tax: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center space-x-2 mt-6">
                    <input
                      type="checkbox"
                      id="visiting_faculty"
                      checked={employeeFormData.visiting_faculty}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, visiting_faculty: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="visiting_faculty">Visiting Faculty (0 Professional Tax)</Label>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Academic Info Tab - Subject Assignment */}
            <TabsContent value="academic" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label>Assigned Subjects by Course & Semester</Label>
                  <p className="text-sm text-gray-600 mb-2">
                    Select subjects that this employee can teach or manage
                  </p>
                  {subjects.length === 0 && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Loading subjects... If this persists, please refresh the page.</span>
                      </div>
                    </div>
                  )}
                    
                    <div className="mt-2 max-h-60 overflow-y-auto border rounded p-3">
                      
                      {/* Group subjects by stream, year, and semester - like Smart Timetable Builder */}
                      {(() => {
                        // Group subjects by stream, year, and semester
                        const grouped = {};
                        
                        subjects.forEach(subject => {
                          const stream = subject.stream || 'Other';
                          const year = subject.year || 'Other';
                          const semester = subject.semester || 'Other';
                          
                          if (!grouped[stream]) {
                            grouped[stream] = {};
                          }
                          if (!grouped[stream][year]) {
                            grouped[stream][year] = {};
                          }
                          if (!grouped[stream][year][semester]) {
                            grouped[stream][year][semester] = [];
                          }
                          
                          grouped[stream][year][semester].push(subject);
                        });

                        // Sort subjects within each group
                        Object.keys(grouped).forEach(stream => {
                          Object.keys(grouped[stream]).forEach(year => {
                            Object.keys(grouped[stream][year]).forEach(semester => {
                              grouped[stream][year][semester].sort((a, b) => a.name.localeCompare(b.name));
                            });
                          });
                        });

                        // Define year order
                        const getYearOrder = (stream) => {
                          if (stream === 'MBA Design Management') {
                            return ['Year 1', 'Year 2'];
                          } else {
                            return ['FY', 'SY', 'TY', 'Fourth Year', 'PG FY', 'PG SY', 'PG First Year', 'PG Second Year'];
                          }
                        };

                        // Define semester order
                        const getSemesterOrder = (year, stream) => {
                          if (stream === 'MBA Design Management') {
                            if (year === 'Year 1' || year === 'PG FY') return ['PG Sem 1', 'PG Sem 2'];
                            if (year === 'Year 2' || year === 'PG SY') return ['PG Sem 3', 'PG Sem 4'];
                          } else {
                            if (year === 'FY') return ['Semester 1', 'Semester 2'];
                            if (year === 'SY') return ['Semester 3', 'Semester 4'];
                            if (year === 'TY') return ['Semester 5', 'Semester 6'];
                            if (year === 'Fourth Year') return ['Semester 7', 'Semester 8'];
                            if (year === 'PG FY' || year === 'PG First Year') return ['PG Sem 1', 'PG Sem 2'];
                            if (year === 'PG SY' || year === 'PG Second Year') return ['PG Sem 3', 'PG Sem 4'];
                          }
                          return [];
                        };

                        // Render grouped subjects
                        return Object.keys(grouped).sort().map(stream => {
                          const yearOrder = getYearOrder(stream);
                          
                          return (
                            <div key={stream} className="mb-4">
                              <div className="px-2 py-2 text-xs font-bold text-blue-600 uppercase tracking-wide border-b mb-2">
                                {stream}
                              </div>
                              {stream === 'Skill Enhancement' ? (
                                <div className="grid grid-cols-2 gap-2 ml-4">
                                  {(grouped[stream]['Other']?.['Other'] || []).concat(
                                    Object.keys(grouped[stream]).reduce((acc, y) => {
                                      if (y === 'Other') return acc;
                                      Object.keys(grouped[stream][y]).forEach(s => {
                                        acc.push(...grouped[stream][y][s]);
                                      });
                                      return acc;
                                    }, [])
                                  ).sort((a, b) => a.name.localeCompare(b.name)).map((subject) => (
                                    <label key={subject.id} className="flex items-center space-x-2 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={employeeFormData.subjects && employeeFormData.subjects.includes(subject.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setEmployeeFormData(prev => ({
                                              ...prev,
                                              subjects: [...(prev.subjects || []), subject.id]
                                            }));
                                          } else {
                                            setEmployeeFormData(prev => ({
                                              ...prev,
                                              subjects: (prev.subjects || []).filter(id => id !== subject.id)
                                            }));
                                          }
                                        }}
                                        className="rounded"
                                      />
                                      <span className="text-xs">{subject.name}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                              yearOrder.map(year => {
                                if (!grouped[stream][year]) return null;
                                
                                const semesterOrder = getSemesterOrder(year, stream);
                                
                                return semesterOrder.map(semester => {
                                  if (!grouped[stream][year][semester]) return null;
                                  
                                  // Get year label
                                  const getYearLabel = (year) => {
                                    if (year === 'FY') return 'First Year';
                                    if (year === 'SY') return 'Second Year';
                                    if (year === 'TY') return 'Third Year';
                                    if (year === 'Fourth Year') return 'Fourth Year';
                                    if (year === 'PG FY' || year === 'PG First Year') return 'PG First Year';
                                    if (year === 'PG SY' || year === 'PG Second Year') return 'PG Second Year';
                                    return year; // For MBA Year 1, Year 2
                                  };
                                  
                                  return (
                                    <div key={`${stream}-${year}-${semester}`} className="mb-3">
                                      <div className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 border-l-2 border-blue-400 mb-2">
                                        {getYearLabel(year)} - {semester}
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 ml-4">
                                        {grouped[stream][year][semester].map((subject) => (
                                          <label key={subject.id} className="flex items-center space-x-2 text-xs">
                                            <input
                                              type="checkbox"
                                              checked={employeeFormData.subjects && employeeFormData.subjects.includes(subject.id)}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setEmployeeFormData(prev => ({
                                                    ...prev,
                                                    subjects: [...(prev.subjects || []), subject.id]
                                                  }));
                                                } else {
                                                  setEmployeeFormData(prev => ({
                                                    ...prev,
                                                    subjects: (prev.subjects || []).filter(id => id !== subject.id)
                                                  }));
                                                }
                                              }}
                                              className="rounded"
                                            />
                                            <span className="text-xs">{subject.name}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                });
                              })
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                  
                  {/* Professional Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Qualifications</Label>
                      <Textarea
                        placeholder="Enter qualifications"
                        value={employeeFormData.qualifications}
                        onChange={(e) => setEmployeeFormData(prev => ({ ...prev, qualifications: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Specialization</Label>
                      <Textarea
                        placeholder="Enter specialization areas"
                        value={employeeFormData.specialization}
                        onChange={(e) => setEmployeeFormData(prev => ({ ...prev, specialization: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      placeholder="Enter years of experience"
                      value={employeeFormData.experience_years}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, experience_years: e.target.value }))}
                    />
                  </div>
                </div>
            </TabsContent>

            {/* Login Details Tab - Only visible to super admin */}
            <TabsContent value="login" className="space-y-4">
              {isSuperAdmin && (user.email !== employeeFormData.email) ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Login credentials are optional. If provided, the employee will be able to log in to the system.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>User ID (Email)</Label>
                      <div className="flex space-x-2">
                        <Input
                          placeholder="Same as email"
                          value={employeeFormData.email || ''}
                          readOnly
                          className="bg-gray-50"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateUserCredentials}
                          className="whitespace-nowrap"
                          title="Generate password"
                        >
                          Generate Password
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Email will be used as User ID</p>
                    </div>
                    <div>
                      <Label>Password</Label>
                      <div className="flex space-x-2">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Default: FirstName@123"
                          value={employeeFormData.password || ''}
                          onChange={(e) => setEmployeeFormData(prev => ({ ...prev, password: e.target.value }))}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowPassword(!showPassword)}
                          className="whitespace-nowrap"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_admin"
                      checked={employeeFormData.is_admin}
                      onChange={(e) => setEmployeeFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="is_admin">Grant admin privileges</Label>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {isSuperAdmin 
                      ? "You cannot manage your own login credentials."
                      : "Only super administrators can manage login credentials."
                    }
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddEmployeeModalOpen(false);
                setEditingEmployee(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEmployee}>
              {editingEmployee ? 'Update Employee' : 'Add Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Dashboard Modal */}
      <Dialog open={showEmployeeDashboardModal} onOpenChange={setShowEmployeeDashboardModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Grid3X3 className="w-5 h-5 mr-2 text-blue-600" />
              Employee Dashboard - {selectedEmployeeForDashboard?.name}
            </DialogTitle>
            <DialogDescription>
              Comprehensive overview of employee's academic activities, attendance, and performance
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployeeForDashboard && (
            <div className="space-y-6">
              {/* Employee Basic Info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <User className="w-8 h-8 text-blue-600 mr-3" />
                    <div>
                      <h3 className="font-semibold text-blue-900">Employee ID</h3>
                      <p className="text-blue-700">{employeeDashboardData?.employee?.employee_id || selectedEmployeeForDashboard.employee_id || 'Loading...'}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Building className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                      <h3 className="font-semibold text-green-900">Department</h3>
                      <p className="text-green-700">
                        {employeeDashboardData?.employee?.departments 
                          ? (Array.isArray(employeeDashboardData.employee.departments) 
                              ? employeeDashboardData.employee.departments.join(', ') 
                              : employeeDashboardData.employee.department)
                          : (selectedEmployeeForDashboard.departments || selectedEmployeeForDashboard.department || 'Not assigned')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <Shield className="w-8 h-8 text-purple-600 mr-3" />
                    <div>
                      <h3 className="font-semibold text-purple-900">Role</h3>
                      <p className="text-purple-700">
                        {(employeeDashboardData?.employee?.role || selectedEmployeeForDashboard?.role) === 'teaching_staff' ? 'Teaching Staff' : 'Administration'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assigned Subjects - Show if any exist */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Assigned Subjects
                </h3>
                {(() => {
                  const employee = employeeDashboardData?.employee || selectedEmployeeForDashboard;
                  const subjectsList = employee?.subjects || [];
                  const assignedSubjects = getEmployeeSubjects(subjectsList);
                  
                  console.log('Employee role:', employee?.role);
                  console.log('Subjects array:', subjectsList);
                  console.log('Assigned subjects:', assignedSubjects);
                  
                  if (assignedSubjects.length === 0) {
                    return <p className="text-gray-500">No subjects assigned yet</p>;
                  }
                  
                  return (
                    <div className="grid grid-cols-2 gap-4">
                      {assignedSubjects.map((subject) => (
                        <div key={subject.id} className="bg-white p-3 rounded border">
                          <h4 className="font-medium text-gray-900">{subject.name}</h4>
                          <p className="text-sm text-gray-600">
                            {[subject.stream, subject.year, subject.semester].filter(Boolean).join(' - ')}
                          </p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Leave Statistics - Simplified */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Approved Leaves Summary */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Leave Summary (Academic Year {employeeDashboardData?.leaves?.academic_year || new Date().getFullYear()})
                  </h3>
                  <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Total Approved Leaves Availed</p>
                      <p className="text-4xl font-bold text-green-600">
                        {employeeDashboardData?.leaves?.approved_leaves_count || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">January - December {employeeDashboardData?.leaves?.academic_year || new Date().getFullYear()}</p>
                    </div>
                  </div>
                </div>
                
                {/* Staff Holidays */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Staff Holidays ({employeeDashboardData?.leaves?.staff_holidays_count || 0} days)
                  </h3>
                  {employeeDashboardData?.leaves?.staff_holidays && employeeDashboardData.leaves.staff_holidays.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto bg-white p-3 rounded border">
                      <div className="space-y-2">
                        {employeeDashboardData.leaves.staff_holidays.map((holiday, index) => (
                          <div key={index} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2">
                            <div>
                              <p className="font-medium text-gray-900">{holiday.name}</p>
                              <p className="text-xs text-gray-500">{holiday.day}</p>
                            </div>
                            <p className="text-sm text-blue-600">{new Date(holiday.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No staff holidays defined</p>
                  )}
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Employment Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Employment Type:</span>
                      <span className="font-semibold text-orange-800">
                        {employeeDashboardData.employee.employment_type || 'Full-time'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Salary Type:</span>
                      <span className="font-semibold text-orange-800">
                        {employeeDashboardData.employee.salary_type || 'Monthly'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Base Salary:</span>
                      <span className="font-semibold text-green-600">
                        ₹{employeeDashboardData.employee.base_salary?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Status:</span>
                      <span className={`font-semibold ${
                        employeeDashboardData.employee.is_active !== false 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {employeeDashboardData.employee.is_active !== false ? (
                          <><CheckCircle className="w-4 h-4 inline mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-4 h-4 inline mr-1" />Inactive</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Today's Classes Schedule */}
              {employeeDashboardData?.schedule && employeeDashboardData.schedule.length > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Today's Classes ({new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })})
                  </h3>
                  {employeeDashboardData.schedule.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white rounded border">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2 text-left">Time Slot</th>
                            <th className="px-4 py-2 text-left">Subject</th>
                            <th className="px-4 py-2 text-left">Classroom</th>
                            <th className="px-4 py-2 text-left">Batch</th>
                            <th className="px-4 py-2 text-left">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeDashboardData.schedule.map((session, index) => (
                            <tr key={session.id || index} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{session.time_slot || 'N/A'}</td>
                              <td className="px-4 py-2">{session.subject_name || session.subject || 'N/A'}</td>
                              <td className="px-4 py-2">{session.classroom_name || session.classroom || 'N/A'}</td>
                              <td className="px-4 py-2">{session.batch_name || session.batch || 'N/A'}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  session.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  session.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {session.status || 'Scheduled'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No classes scheduled for today</p>
                  )}
                </div>
              )}
              
              {/* Show message when no classes today */}
              {(!employeeDashboardData?.schedule || employeeDashboardData.schedule.length === 0) && (
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-center flex items-center justify-center">
                    <Clock className="w-5 h-5 mr-2" />
                    No classes scheduled for today
                  </p>
                </div>
              )}

              {/* Contact and Bank Information */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Email:</strong> {employeeDashboardData.employee.email}</div>
                    <div><strong>Phone:</strong> {employeeDashboardData.employee.phone || 'Not provided'}</div>
                    <div><strong>Address:</strong> {employeeDashboardData.employee.address || 'Not provided'}</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Bank Details</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Account Number:</strong> {employeeDashboardData.employee.bank_account || 'Not provided'}</div>
                    <div><strong>IFSC Code:</strong> {employeeDashboardData.employee.ifsc_code || 'Not provided'}</div>
                    <div><strong>Bank Name:</strong> {employeeDashboardData.employee.bank_name || 'Not provided'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDashboardModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesManagement;